import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Settings2, Package, Users, FileText, BarChart3, LogOut, ArrowLeft, LoaderCircle, Download, Save } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { api, clearToken, formatApiError } from "../lib/api";
import { ContentTab } from "../components/settings/ContentTab";
import { ClicksTab } from "../components/settings/ClicksTab";

const inputCls = "h-10 w-full rounded-xl border border-[#E4DFD3] bg-white px-3.5 text-sm text-[#1A1D1A] outline-none transition-colors focus:border-[#576856]";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#6D736D]";

const ConfigTab = () => {
    const [form, setForm] = useState(null);
    const [domains, setDomains] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/config").then(({ data }) => {
            setForm({ affiliate_tag: data.affiliate_tag || "", amazon_domain: data.amazon_domain || "amazon.com", adsense_publisher_id: data.adsense_publisher_id || "", google_tag_id: data.google_tag_id || "" });
            setDomains(data.amazon_domains || []);
        }).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/config", form);
            toast.success("Settings saved.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!form) return <div className="flex justify-center py-16"><LoaderCircle className="animate-spin text-[#718370]" /></div>;

    return (
        <div className="space-y-5">
            <div>
                <label className={labelCls} htmlFor="cfg-tag">Amazon affiliate tag</label>
                <input id="cfg-tag" data-testid="config-amazon-tag-input" className={inputCls} placeholder="e.g. smartbreath-20" value={form.affiliate_tag}
                    onChange={(e) => setForm({ ...form, affiliate_tag: e.target.value })} />
                <p className="mt-1.5 text-xs text-[#6D736D]">Appended to every Amazon link. Leave empty to omit the tag.</p>
            </div>
            <div>
                <label className={labelCls} htmlFor="cfg-domain">Amazon domain</label>
                <select id="cfg-domain" data-testid="config-amazon-domain-select" className={inputCls} value={form.amazon_domain}
                    onChange={(e) => setForm({ ...form, amazon_domain: e.target.value })}>
                    {domains.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>
            <div>
                <label className={labelCls} htmlFor="cfg-adsense">AdSense publisher ID</label>
                <input id="cfg-adsense" data-testid="config-adsense-pubid-input" className={inputCls} placeholder="ca-pub-XXXXXXXXXXXXXXXX" value={form.adsense_publisher_id}
                    onChange={(e) => setForm({ ...form, adsense_publisher_id: e.target.value })} />
                <p className="mt-1.5 text-xs text-[#6D736D]">When set, live AdSense units replace the “Advertisement” placeholders.</p>
            </div>
            <div>
                <label className={labelCls} htmlFor="cfg-gtag">Google tag ID (Ads / Analytics)</label>
                <input id="cfg-gtag" data-testid="config-google-tag-input" className={inputCls} placeholder="G-XXXXXXX or AW-XXXXXXX" value={form.google_tag_id || ""}
                    onChange={(e) => setForm({ ...form, google_tag_id: e.target.value })} />
                <p className="mt-1.5 text-xs text-[#6D736D]">When set, the Google tag (gtag.js) loads on every page — required for Google Ads conversion tracking and PPC campaigns.</p>
            </div>
            <button data-testid="config-save-btn" onClick={save} disabled={saving}
                className="flex items-center gap-2 rounded-full bg-[#576856] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1A1D1A] disabled:opacity-70">
                {saving ? <LoaderCircle size={15} className="animate-spin" /> : <Save size={15} />}
                Save settings
            </button>
        </div>
    );
};

const ProductsTab = () => {
    const [products, setProducts] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/products").then(({ data }) => setProducts(data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const update = (id, field, value) => setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

    const save = async () => {
        setSaving(true);
        try {
            const payload = products.map(({ id, category, name, badge, tagline, description, search, asin, image, icon, recommended, highlighted, has_button, order }) =>
                ({ id, category, name, badge, tagline, description, search, asin: asin || "", image, icon, recommended, highlighted, has_button, order }));
            await api.put("/products", payload);
            toast.success("Products saved.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!products) return <div className="flex justify-center py-16"><LoaderCircle className="animate-spin text-[#718370]" /></div>;

    return (
        <div className="space-y-6">
            {products.map((p) => (
                <div key={p.id} data-testid={`product-editor-${p.id}`} className="rounded-2xl border border-[#E4DFD3] bg-white p-5">
                    <div className="mb-4 flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-[#718370]">{p.id} · {p.category}</span>
                        <span className="text-xs text-[#6D736D]">{p.clicks ?? 0} clicks</span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <label className={labelCls}>Name</label>
                            <input className={inputCls} value={p.name} onChange={(e) => update(p.id, "name", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>Badge</label>
                            <input className={inputCls} value={p.badge} onChange={(e) => update(p.id, "badge", e.target.value)} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className={labelCls}>Tagline</label>
                            <input className={inputCls} value={p.tagline} onChange={(e) => update(p.id, "tagline", e.target.value)} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className={labelCls}>Description</label>
                            <textarea className="w-full rounded-xl border border-[#E4DFD3] bg-white px-3.5 py-2.5 text-sm text-[#1A1D1A] outline-none transition-colors focus:border-[#576856]" rows={3}
                                value={p.description} onChange={(e) => update(p.id, "description", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>Amazon search terms</label>
                            <input className={inputCls} value={p.search} onChange={(e) => update(p.id, "search", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>ASIN (optional — overrides search)</label>
                            <input className={inputCls} value={p.asin || ""} placeholder="e.g. B08XYZ1234" onChange={(e) => update(p.id, "asin", e.target.value)} />
                        </div>
                        {p.category === "essential" && (
                            <div className="sm:col-span-2">
                                <label className={labelCls}>Image URL</label>
                                <input className={inputCls} value={p.image} onChange={(e) => update(p.id, "image", e.target.value)} />
                            </div>
                        )}
                        <div className="flex flex-wrap gap-5 sm:col-span-2">
                            {p.category === "essential" && (
                                <label className="flex items-center gap-2 text-sm text-[#1A1D1A]">
                                    <input type="checkbox" className="h-4 w-4 accent-[#576856]" checked={!!p.recommended} onChange={(e) => update(p.id, "recommended", e.target.checked)} />
                                    Recommended pick
                                </label>
                            )}
                            {p.category === "analyzer" && (
                                <>
                                    <label className="flex items-center gap-2 text-sm text-[#1A1D1A]">
                                        <input type="checkbox" className="h-4 w-4 accent-[#576856]" checked={!!p.highlighted} onChange={(e) => update(p.id, "highlighted", e.target.checked)} />
                                        Highlighted
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-[#1A1D1A]">
                                        <input type="checkbox" className="h-4 w-4 accent-[#576856]" checked={!!p.has_button} onChange={(e) => update(p.id, "has_button", e.target.checked)} />
                                        Show buy button
                                    </label>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            <button data-testid="products-save-btn" onClick={save} disabled={saving}
                className="flex items-center gap-2 rounded-full bg-[#576856] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1A1D1A] disabled:opacity-70">
                {saving ? <LoaderCircle size={15} className="animate-spin" /> : <Save size={15} />}
                Save products
            </button>
        </div>
    );
};

const SubscribersTab = () => {
    const [subs, setSubs] = useState(null);

    useEffect(() => {
        api.get("/subscribers").then(({ data }) => setSubs(data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const exportCsv = () => {
        const rows = [["email", "subscribed_at"], ...subs.map((s) => [s.email, s.created_at])];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "smartbreathcheck-subscribers.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!subs) return <div className="flex justify-center py-16"><LoaderCircle className="animate-spin text-[#718370]" /></div>;

    return (
        <div>
            <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-[#6D736D]" data-testid="subscribers-count">{subs.length} subscriber{subs.length === 1 ? "" : "s"}</p>
                <button data-testid="export-subscribers-csv-btn" onClick={exportCsv} disabled={subs.length === 0}
                    className="flex items-center gap-2 rounded-full border border-[#C4BCA8] px-4 py-2 text-xs font-semibold text-[#576856] transition-colors hover:bg-[#F5F2EA] disabled:opacity-50">
                    <Download size={14} /> Export CSV
                </button>
            </div>
            {subs.length === 0 ? (
                <p className="rounded-xl border border-dashed border-[#C4BCA8] bg-white px-4 py-10 text-center text-sm text-[#6D736D]">
                    No subscribers yet — the checklist form on the homepage collects them here.
                </p>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-[#E4DFD3] bg-white">
                    <table data-testid="subscribers-table" className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-[#E4DFD3] bg-[#F5F2EA]">
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#6D736D]">Email</th>
                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#6D736D]">Subscribed</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E4DFD3]">
                            {subs.map((s) => (
                                <tr key={s.id}>
                                    <td className="px-4 py-3 font-medium text-[#1A1D1A]">{s.email}</td>
                                    <td className="px-4 py-3 text-[#6D736D]">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const TABS = [
    { id: "config", label: "Settings", icon: Settings2 },
    { id: "products", label: "Products", icon: Package },
    { id: "content", label: "Content", icon: FileText },
    { id: "clicks", label: "Clicks", icon: BarChart3 },
    { id: "subscribers", label: "Subscribers", icon: Users },
];

const SettingsDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(location.state?.user || null);
    const [tab, setTab] = useState("config");

    useEffect(() => {
        if (location.state?.user) return;
        api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => { clearToken(); navigate("/login"); });
    }, [navigate, location.state]);

    if (!user) {
        return <div className="flex min-h-screen items-center justify-center bg-[#F5F2EA]"><LoaderCircle className="animate-spin text-[#718370]" size={28} /></div>;
    }

    return (
        <div data-testid="settings-dashboard" className="min-h-screen bg-[#F5F2EA]">
            <header className="border-b border-[#E4DFD3] bg-[#FDFCF8]">
                <div className="mx-auto flex h-16 max-w-[960px] items-center justify-between px-4 sm:px-6">
                    <Logo />
                    <div className="flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-1.5 text-xs font-medium text-[#6D736D] transition-colors hover:text-[#1A1D1A]">
                            <ArrowLeft size={13} /> View site
                        </Link>
                        <button
                            data-testid="logout-btn"
                            onClick={async () => { try { await api.post("/auth/logout"); } catch (e) { /* JWT sessions are client-side only */ } clearToken(); navigate("/login"); }}
                            className="flex items-center gap-1.5 rounded-full border border-[#E4DFD3] px-3.5 py-1.5 text-xs font-semibold text-[#A65656] transition-colors hover:border-[#A65656]/40 hover:bg-[#A65656]/5"
                        >
                            <LogOut size={13} /> Log out
                        </button>
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-[960px] px-4 sm:px-6 py-10">
                <h1 className="font-serif text-3xl font-semibold text-[#1A1D1A]">Owner settings</h1>
                <p className="mt-1 text-sm text-[#6D736D]">Signed in as {user.email}</p>
                <div className="mt-8 flex gap-2 border-b border-[#E4DFD3]">
                    {TABS.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            data-testid={`settings-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                                tab === id ? "border-[#576856] text-[#1A1D1A]" : "border-transparent text-[#6D736D] hover:text-[#1A1D1A]"
                            }`}
                        >
                            <Icon size={15} strokeWidth={1.8} /> {label}
                        </button>
                    ))}
                </div>
                <div className="mt-8">
                    {tab === "config" && <ConfigTab />}
                    {tab === "products" && <ProductsTab />}
                    {tab === "content" && <ContentTab />}
                    {tab === "clicks" && <ClicksTab />}
                    {tab === "subscribers" && <SubscribersTab />}
                </div>
            </main>
        </div>
    );
};

export default SettingsDashboard;
